import React, { useCallback, useMemo } from 'react';
import { Loader, Text, Timeline } from '@wix/design-system';
import { CircleSmallFilled, StatusCompleteFilled } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import styles from './LaunchStepsList.scss';
import { LaunchStep } from '../../../config/launchSteps';

export interface LaunchStepsListProps {
  steps: LaunchStep[];
  currentStep: number;
}

export function LaunchStepsList({ steps, currentStep }: LaunchStepsListProps) {
  const { t } = useTranslation();

  const getPrefix = useCallback(
    (index: number) => {
      const isCompleted = index < currentStep;
      const isActive = index === currentStep;

      if (isCompleted) {
        return <StatusCompleteFilled />;
      }

      if (isActive) {
        return <Loader size="tiny" />;
      }

      return <CircleSmallFilled />;
    },
    [currentStep],
  );

  const renderLabel = useCallback(
    (step: LaunchStep, index: number) => {
      const isStale = index > currentStep;
      return (
        <div className={styles.stepLabel}>
          <Text secondary={isStale} light={isStale}>
            {t(step.labelKey)}
          </Text>
        </div>
      );
    },
    [currentStep, t],
  );

  const items = useMemo(
    () =>
      steps.map((step, index) => ({
        label: renderLabel(step, index),
        customPrefix: <div className={styles.stepPrefix}>{getPrefix(index)}</div>,
      })),
    [steps, getPrefix, renderLabel],
  );

  if (!steps?.length) {
    return null;
  }

  return <Timeline gap="40px" items={items} />;
}
